/**
 * Collection PLP card behaviours.
 * Section: sections/cclp-plp-test-grid.liquid
 *
 * MutationObserver re-bind pattern keeps handlers alive after Section Rendering
 * API filter/sort fetches via `<collection-info>`.
 */
(() => {
  'use strict';
  if (customElements.get('cclp-plp-grid')) return;

  customElements.define(
    'cclp-plp-grid',
    class extends HTMLElement {
      connectedCallback() {
        if (this._initialized) return;
        this._initialized = true;
        const self = this;

        const sectionId = self.dataset.sectionId;
        const section = document.getElementById('shopify-section-' + sectionId);
        if (!section) return;

        const escHtml = (s) =>
          s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

        const abbreviate = (n) => {
          if (!n || isNaN(n)) return '';
          if (n >= 1000000) return (n / 1000000).toFixed(n % 1000000 === 0 ? 0 : 1) + 'M';
          if (n >= 10000) return Math.round(n / 1000) + 'K';
          if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
          return String(n);
        };

        const parseCount = (txt) => {
          txt = (txt || '').replace(/,/g, '').trim();
          let m = txt.match(/([0-9]+(?:\.[0-9]+)?)\s*[mM]/);
          if (m) return Math.round(parseFloat(m[1]) * 1000000);
          m = txt.match(/([0-9]+(?:\.[0-9]+)?)\s*[kK]/);
          if (m) return Math.round(parseFloat(m[1]) * 1000);
          const n = parseInt(txt.replace(/[^0-9]/g, ''), 10);
          return isNaN(n) ? 0 : n;
        };

        const compactEl = (el) => {
          const txt = (el.textContent || '').trim();
          const n = parseCount(txt);
          if (!n) return;
          const compact = abbreviate(n) + ' Reviews';
          if (txt === compact) return;
          el.textContent = compact;
        };

        const compactAll = () => {
          section.querySelectorAll('.junip-product-summary-review-count').forEach(compactEl);
        };

        let canHover = false;
        try {
          canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
        } catch (err) {
          canHover = false;
        }

        const swapImageFor = (swatch) => {
          const card = swatch.closest('.cclp-plp-card');
          if (!card) return;
          const newSrc = swatch.getAttribute('data-variant-image');
          const newSrcset = swatch.getAttribute('data-variant-srcset');
          if (!newSrc) return;

          const allImgs = card.querySelectorAll('.product__card-image');
          if (!allImgs.length) return;

          card.querySelectorAll('.cclp-plp-card__swatch').forEach((s) => s.classList.remove('is-active'));
          swatch.classList.add('is-active');

          const meta = card.querySelector('.cclp-plp-card__meta');
          let shadeName = swatch.getAttribute('title') || swatch.getAttribute('aria-label') || '';
          shadeName = shadeName.replace(/^View\s+/i, '').trim();
          if (meta && shadeName) {
            if (!meta.dataset.defaultText) meta.dataset.defaultText = meta.textContent.trim();
            const desc = swatch.getAttribute('data-description') || '';
            meta.innerHTML = desc
              ? '<strong style="font-family:ArticulatCF,GeogrotesqueCondBoldWeb,Arial Black,Impact,sans-serif;font-weight:700;text-transform:uppercase;letter-spacing:.3px">' +
                escHtml(shadeName) +
                '</strong> \u2013 ' +
                escHtml(desc)
              : escHtml(shadeName);
          }

          const preload = new Image();
          if (newSrcset) preload.srcset = newSrcset;
          preload.src = newSrc;
          const doSwap = () => {
            allImgs.forEach((img) => {
              if (newSrcset) img.srcset = newSrcset;
              img.src = newSrc;
              img.removeAttribute('sizes');
            });
          };
          if (preload.complete && preload.naturalWidth > 0) doSwap();
          else {
            preload.onload = doSwap;
            preload.onerror = doSwap;
          }
        };

        const handleSwatchEvent = (e) => {
          const swatch = e.target && e.target.closest ? e.target.closest('.cclp-plp-card__swatch') : null;
          if (!swatch || !section.contains(swatch)) return;
          e.stopPropagation();
          if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
          if (e.type === 'click') {
            e.preventDefault();
            swapImageFor(swatch);
          } else if (e.type === 'pointerdown' && canHover && e.pointerType && e.pointerType !== 'touch') {
            swapImageFor(swatch);
          }
        };

        ['pointerdown', 'mousedown', 'click'].forEach((type) => {
          section.addEventListener(type, handleSwatchEvent, { capture: true, passive: false });
        });

        const bindSwatchClickHandlers = () => {
          section.querySelectorAll('.cclp-plp-card__swatch').forEach((swatch) => {
            if (swatch.__cclpSwatchBound) return;
            swatch.__cclpSwatchBound = true;
            swatch.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();
              if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
              swapImageFor(swatch);
            });
          });
        };
        bindSwatchClickHandlers();

        if (canHover) {
          section.addEventListener(
            'mouseover',
            (e) => {
              const overlay = e.target && e.target.closest ? e.target.closest('.cclp-plp-card__swatches--overlay') : null;
              if (!overlay || !section.contains(overlay)) return;
              const card = overlay.closest('.cclp-plp-card');
              if (!card) return;
              card.classList.add('is-swatching');
              const swatch = e.target.closest('.cclp-plp-card__swatch');
              if (!swatch) return;
              if (!card.dataset.swatchOrigSet) {
                card.querySelectorAll('.product__card-image').forEach((o) => {
                  o.dataset.swatchOrigSrc = o.src || '';
                  o.dataset.swatchOrigSrcset = o.srcset || '';
                  o.dataset.swatchOrigSizes = o.getAttribute('sizes') || '';
                });
                card.dataset.swatchOrigSet = '1';
                const origMeta = card.querySelector('.cclp-plp-card__meta');
                if (origMeta) card.dataset.swatchOrigMeta = origMeta.textContent.trim();
              }
              swapImageFor(swatch);
            },
            { passive: true }
          );

          section.addEventListener(
            'mouseout',
            (e) => {
              const overlay = e.target && e.target.closest ? e.target.closest('.cclp-plp-card__swatches--overlay') : null;
              if (!overlay) return;
              if (e.relatedTarget && overlay.contains(e.relatedTarget)) return;
              const card = overlay.closest('.cclp-plp-card');
              if (!card) return;
              card.classList.remove('is-swatching');
              if (!card.dataset.swatchOrigSet) return;
              card.querySelectorAll('.product__card-image').forEach((img) => {
                if (img.dataset.swatchOrigSrcset) img.srcset = img.dataset.swatchOrigSrcset;
                if (img.dataset.swatchOrigSrc) img.src = img.dataset.swatchOrigSrc;
                if (img.dataset.swatchOrigSizes) img.setAttribute('sizes', img.dataset.swatchOrigSizes);
                delete img.dataset.swatchOrigSrc;
                delete img.dataset.swatchOrigSrcset;
                delete img.dataset.swatchOrigSizes;
              });
              const meta = card.querySelector('.cclp-plp-card__meta');
              if (meta && card.dataset.swatchOrigMeta) meta.textContent = card.dataset.swatchOrigMeta;
              const swatches = card.querySelectorAll('.cclp-plp-card__swatches--overlay .cclp-plp-card__swatch');
              swatches.forEach((s, i) => s.classList.toggle('is-active', i === 0));
              delete card.dataset.swatchOrigSet;
              delete card.dataset.swatchOrigMeta;
            },
            { passive: true }
          );
        }

        self._compactAll = compactAll;
        self._bindSwatchClickHandlers = bindSwatchClickHandlers;
        self._canHover = canHover;
        self._escHtml = escHtml;
        self._section = section;

        const productGrid = section.querySelector('#product-grid-' + sectionId);

        const revealStars = () => {
          section.querySelectorAll('.product__card-stars').forEach((wrap) => {
            const countEl = wrap.querySelector('.junip-product-summary-review-count');
            if (!countEl) return;
            if (!(countEl.textContent || countEl.innerHTML || '').trim()) return;
            wrap.style.opacity = '1';
          });
        };

        const triggerJunip = () => {
          if (window.junip && typeof window.junip.refreshWidgets === 'function') {
            try {
              window.junip.refreshWidgets();
            } catch (e) {}
          }
          if (window.junip && typeof window.junip.storefrontStarsLoader === 'function') {
            try {
              window.junip.storefrontStarsLoader();
            } catch (e) {}
          } else if (window.junipLoader && typeof window.junipLoader.load === 'function') {
            try {
              window.junipLoader.load();
            } catch (e) {}
          }
          revealStars();
        };

        const refreshCardStars = () => {
          compactAll();
          revealStars();
          triggerJunip();
        };

        const scheduleJunipRefresh = () => {
          refreshCardStars();
          setTimeout(refreshCardStars, 300);
        };

        scheduleJunipRefresh();

        const isBreakoutOnlyMutation = (mutations) => {
          const added = mutations.flatMap((m) =>
            m.type === 'childList' ? [...m.addedNodes].filter((n) => n.nodeType === 1) : []
          );
          if (!added.length) return false;
          return added.every((n) => n.classList?.contains('breakout-inserted'));
        };

        let pending = false;
        self._observer = new MutationObserver((mutations) => {
          const gridUpdated =
            productGrid &&
            mutations.some((m) => {
              if (m.type !== 'childList') return false;
              return m.target === productGrid || productGrid.contains(m.target);
            });

          if (pending) return;
          pending = true;
          requestAnimationFrame(() => {
            pending = false;
            compactAll();
            revealStars();
            bindSwatchClickHandlers();
            if (typeof self._bindQuickshopClickHandlers === 'function') self._bindQuickshopClickHandlers();
            if (gridUpdated && !isBreakoutOnlyMutation(mutations)) scheduleJunipRefresh();
          });
        });
        self._observer.observe(section, { childList: true, subtree: true, characterData: true });

        self._initQuickShop();
      }

      _initQuickShop() {
        const self = this;
        const section = self._section;
        const escHtml = self._escHtml;
        const canHover = self._canHover;

        const quickshopEl = section.querySelector('.cclp-plp-quickshop');
        if (!quickshopEl) return;

        const quickshopCache = {};
        const quickshopInflight = {};
        let quickshopCurrent = null;
        let lastTrigger = null;

        const $ = (sel) => quickshopEl.querySelector(sel);
        const loadingEl = $('.cclp-plp-quickshop__loading');
        const errorEl = $('.cclp-plp-quickshop__error');
        const contentEl = $('.cclp-plp-quickshop__content');
        const successEl = $('.cclp-plp-quickshop__success');
        const imageEl = $('.cclp-plp-quickshop__image');
        const thumbsEl = $('.cclp-plp-quickshop__thumbs');
        const titleEl = $('.cclp-plp-quickshop__title');
        const priceEl = $('.cclp-plp-quickshop__price');
        const compareEl = $('.cclp-plp-quickshop__compare');
        const starsEl = $('.cclp-plp-quickshop__stars');
        const descEl = $('.cclp-plp-quickshop__desc');
        const variantEl = $('.cclp-plp-quickshop__variant');
        const variantNameEl = $('.cclp-plp-quickshop__variant-name');
        const swatchesEl = $('.cclp-plp-quickshop__swatches');
        const sizesEl = $('.cclp-plp-quickshop__sizes');
        const sizeBtnsEl = $('[data-quickshop-sizes]');
        const footerEl = $('[data-quickshop-footer]');
        const addBtn = $('[data-quickshop-add]');
        const addLabelEl = $('.cclp-plp-quickshop__add-label');
        const addCompareEl = $('.cclp-plp-quickshop__add-compare');
        const addPriceEl = $('.cclp-plp-quickshop__add-price');
        const pdpLinks = quickshopEl.querySelectorAll('[data-quickshop-pdp]');
        const reviewsEl = $('[data-quickshop-reviews]');
        const reviewsCache = {};
        const shadeDescCache = {};

        const currencyCode = (section.dataset.currency || 'USD').trim();
        const formatPrice = (cents) => {
          const n = Number(cents) / 100;
          if (isNaN(n)) return '';
          try {
            return new Intl.NumberFormat(undefined, {
              style: 'currency',
              currency: currencyCode,
              minimumFractionDigits: n % 1 === 0 ? 0 : 2,
              maximumFractionDigits: 2,
            }).format(n);
          } catch (e) {
            return '$' + (n % 1 === 0 ? n.toFixed(0) : n.toFixed(2));
          }
        };

        const resetState = () => {
          loadingEl.hidden = true;
          errorEl.hidden = true;
          contentEl.hidden = true;
          successEl.hidden = true;
          if (footerEl) footerEl.hidden = true;
          if (reviewsEl) {
            reviewsEl.hidden = true;
            reviewsEl.innerHTML = '';
          }
          if (imageEl) imageEl.style.opacity = '';
          addBtn.removeAttribute('disabled');
          if (swatchesEl) swatchesEl._qsHoverSwatch = null;
        };
        const showLoading = () => {
          resetState();
          loadingEl.hidden = false;
        };
        const showError = () => {
          resetState();
          errorEl.hidden = false;
        };
        const showContent = () => {
          resetState();
          contentEl.hidden = false;
          if (footerEl) footerEl.hidden = false;
        };

        let closeStateTimer = null;
        const openModal = () => {
          if (closeStateTimer) {
            clearTimeout(closeStateTimer);
            closeStateTimer = null;
          }
          if (imageEl) {
            imageEl._initialSwapDone = false;
            imageEl._swapGen = (imageEl._swapGen || 0) + 1;
            imageEl.classList.remove('is-swapping');
          }
          document.documentElement.classList.add('cclp-plp-quickshop-open');
          document.body.classList.add('cclp-plp-quickshop-open');
          quickshopEl.setAttribute('data-open', 'true');
          quickshopEl.setAttribute('aria-hidden', 'false');
        };
        const closeModal = () => {
          document.documentElement.classList.remove('cclp-plp-quickshop-open');
          document.body.classList.remove('cclp-plp-quickshop-open');
          quickshopEl.removeAttribute('data-open');
          quickshopEl.setAttribute('aria-hidden', 'true');
          if (lastTrigger && typeof lastTrigger.focus === 'function') {
            try {
              lastTrigger.focus({ preventScroll: true });
            } catch (e) {}
          }
          if (closeStateTimer) clearTimeout(closeStateTimer);
          closeStateTimer = setTimeout(() => {
            quickshopCurrent = null;
            resetState();
            closeStateTimer = null;
          }, 320);
        };

        self._quickshop = {
          quickshopEl,
          quickshopCache,
          quickshopInflight,
          getCurrent: () => quickshopCurrent,
          setCurrent: (v) => {
            quickshopCurrent = v;
          },
          getLastTrigger: () => lastTrigger,
          setLastTrigger: (v) => {
            lastTrigger = v;
          },
          formatPrice,
          openModal,
          closeModal,
          showLoading,
          showError,
          showContent,
          resetState,
          els: {
            imageEl, thumbsEl, titleEl, priceEl, compareEl, starsEl, descEl,
            variantEl, variantNameEl, swatchesEl, sizesEl, sizeBtnsEl,
            footerEl, addBtn, addLabelEl, addCompareEl, addPriceEl,
            pdpLinks, reviewsEl,
          },
          reviewsCache,
          shadeDescCache,
          escHtml,
          canHover,
        };

        self._initQuickShopHandlers();
      }

      _initQuickShopHandlers() {
        const self = this;
        const section = self._section;
        const qs = self._quickshop;
        if (!qs) return;
        const { quickshopEl, quickshopCache, quickshopInflight, escHtml, canHover, formatPrice } = qs;
        const { imageEl, thumbsEl, titleEl, priceEl, compareEl, starsEl, descEl, variantEl, variantNameEl,
          swatchesEl, sizesEl, sizeBtnsEl, addBtn, addLabelEl, addCompareEl, addPriceEl, pdpLinks, reviewsEl } = qs.els;

        const renderStars = (rating) => {
          const r = Math.max(0, Math.min(5, Math.round(Number(rating) || 0)));
          const filled = '<svg width="18" height="17" viewBox="0 0 18 17" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5.36372 16.7222L5.73622 11.5359L0.585938 10.7443L4.87784 7.81986L2.2865 3.31213L7.27483 4.83087L9.18594 0L11.097 4.83087L16.0853 3.31213L13.4941 7.81986L17.7859 10.7443L12.6357 11.5359L13.0082 16.7222L9.18594 13.2L5.36372 16.7222Z" fill="#000"/></svg>';
          const empty = '<svg width="18" height="17" viewBox="0 0 18 17" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5.36372 16.7222L5.73622 11.5359L0.585938 10.7443L4.87784 7.81986L2.2865 3.31213L7.27483 4.83087L9.18594 0L11.097 4.83087L16.0853 3.31213L13.4941 7.81986L17.7859 10.7443L12.6357 11.5359L13.0082 16.7222L9.18594 13.2L5.36372 16.7222Z" fill="#d0d0d0"/></svg>';
          return new Array(r + 1).join(filled) + new Array(5 - r + 1).join(empty);
        };

        const fetchReviews = (productId) => {
          if (!productId) return Promise.resolve(null);
          if (qs.reviewsCache[productId]) return Promise.resolve(qs.reviewsCache[productId]);
          const url = 'https://api.juniphq.com/v1/product_reviews?filter[product_remote_ids]=' + productId + '&include[]=customer&per_page=25';
          const junipKey = section.dataset.junipKey || '4NjxTTX33ohe7HAXvYJi8ccY';
          return fetch(url, { method: 'GET', headers: { 'Junip-Store-Key': junipKey } })
            .then((r) => (r.ok ? r.json() : null))
            .then((data) => {
              qs.reviewsCache[productId] = data;
              return data;
            })
            .catch(() => null);
        };

        const renderReviews = (productId) => {
          if (!reviewsEl) return;
          reviewsEl.hidden = true;
          reviewsEl.innerHTML = '';
          const token = productId;
          fetchReviews(productId).then((data) => {
            const cur = qs.getCurrent();
            if (token !== (cur && cur.product && cur.product.id)) return;
            if (!data || !data.product_reviews || !data.product_reviews.length) return;
            const all = data.product_reviews;
            const featured = all.filter((r) => r.featured);
            let pool = (featured.length >= 2 ? featured : all).filter((r) => (r.rating || 0) >= 4 && (r.body || '').length > 20);
            if (!pool.length) pool = all;
            const top = pool.slice(0, 2);
            let html = '<h3 class="cclp-plp-quickshop__reviews-title">What Customers Say</h3>';
            html += top
              .map((r) => {
                const name = r.customer
                  ? ((r.customer.first_name || '') + (r.customer.last_name ? ' ' + (r.customer.last_name || '').charAt(0) + '.' : '')).trim()
                  : '';
                const verifiedLabel = r.verified_buyer ? '<p class="cclp-plp-quickshop__review-verified">Verified buyer</p>' : '';
                const safeTitle = r.title ? escHtml(String(r.title)) : '';
                const safeBody = r.body ? escHtml(String(r.body)) : '';
                return (
                  '<div class="cclp-plp-quickshop__review">' +
                  (name ? '<p class="cclp-plp-quickshop__review-author">' + name + '</p>' : '') +
                  verifiedLabel +
                  '<span class="cclp-plp-quickshop__review-stars" aria-label="' + (r.rating || 0) + ' out of 5 stars">' + renderStars(r.rating) + '</span>' +
                  (safeTitle ? '<p class="cclp-plp-quickshop__review-title">' + safeTitle + '</p>' : '') +
                  (safeBody ? '<p class="cclp-plp-quickshop__review-body">' + safeBody + '</p>' : '') +
                  '</div>'
                );
              })
              .join('');
            reviewsEl.innerHTML = html;
            reviewsEl.hidden = false;
          });
        };

        const fetchShadeDescriptions = (handle) => {
          if (!handle) return Promise.resolve({});
          if (qs.shadeDescCache[handle]) return Promise.resolve(qs.shadeDescCache[handle]);
          return fetch('/products/' + handle, { headers: { Accept: 'text/html' } })
            .then((r) => (r.ok ? r.text() : ''))
            .then((html) => {
              const map = {};
              if (!html) {
                qs.shadeDescCache[handle] = map;
                return map;
              }
              try {
                const doc = new DOMParser().parseFromString(html, 'text/html');
                doc.querySelectorAll('.product__description-shades .shade-description').forEach((el) => {
                  const nameEl = el.querySelector('.shade-name');
                  if (!nameEl) return;
                  const name = (nameEl.textContent || '').trim();
                  const full = (el.textContent || '').trim();
                  const desc = full.indexOf(name) === 0 ? full.slice(name.length).trim() : full;
                  if (name) map[name.toLowerCase()] = desc;
                });
              } catch (e) {}
              qs.shadeDescCache[handle] = map;
              return map;
            })
            .catch(() => {
              qs.shadeDescCache[handle] = {};
              return {};
            });
        };

        const optionName = (o) => {
          if (!o) return '';
          if (typeof o === 'string') return o.toLowerCase();
          if (typeof o === 'object' && o.name) return String(o.name).toLowerCase();
          return '';
        };

        const detectShadeOptionIndex = (product) => {
          const opts = product.options || [];
          for (let i = 0; i < opts.length; i++) {
            const name = optionName(opts[i]);
            if (['shade', 'shades', 'color', 'colors', 'colour', 'colours'].includes(name)) {
              const distinct = {};
              (product.variants || []).forEach((v) => {
                if (v.options && v.options[i]) distinct[v.options[i]] = true;
              });
              if (Object.keys(distinct).length > 1) return i;
              return -1;
            }
          }
          const variants = product.variants || [];
          if (variants.length > 1) {
            for (let o = 0; o < opts.length; o++) {
              const name2 = optionName(opts[o]);
              if (['title', 'size', 'sizes'].includes(name2)) continue;
              const firstVal = variants[0].options ? variants[0].options[o] : null;
              for (let vi = 1; vi < variants.length; vi++) {
                const val = variants[vi].options ? variants[vi].options[o] : null;
                if (val !== firstVal) return o;
              }
            }
          }
          return -1;
        };

        const detectSizeOptionIndex = (product, shadeIndex) => {
          const opts = product.options || [];
          for (let i = 0; i < opts.length; i++) {
            if (i === shadeIndex) continue;
            const name = optionName(opts[i]);
            if (name === 'size' || name === 'sizes') return i;
          }
          const variants = product.variants || [];
          if (variants.length > 1) {
            for (let o = 0; o < opts.length; o++) {
              if (o === shadeIndex) continue;
              const name2 = optionName(opts[o]);
              if (name2 === 'title') continue;
              const firstVal = variants[0].options ? variants[0].options[o] : null;
              for (let vi = 1; vi < variants.length; vi++) {
                const val = variants[vi].options ? variants[vi].options[o] : null;
                if (val !== firstVal) return o;
              }
            }
          }
          return -1;
        };

        const findVariantIndexBy = (product, shadeIndex, shadeValue, sizeIndex, sizeValue) => {
          const variants = product.variants || [];
          let matchExactAvailable = -1;
          let matchExact = -1;
          let matchShadeAvailable = -1;
          let matchShade = -1;
          for (let i = 0; i < variants.length; i++) {
            const v = variants[i];
            const vShade = shadeIndex >= 0 && v.options ? v.options[shadeIndex] : null;
            const vSize = sizeIndex >= 0 && v.options ? v.options[sizeIndex] : null;
            const shadeOk = shadeIndex < 0 || vShade === shadeValue;
            const sizeOk = sizeIndex < 0 || vSize === sizeValue;
            if (shadeOk && sizeOk) {
              if (matchExact === -1) matchExact = i;
              if (v.available && matchExactAvailable === -1) matchExactAvailable = i;
            }
            if (shadeOk) {
              if (matchShade === -1) matchShade = i;
              if (v.available && matchShadeAvailable === -1) matchShadeAvailable = i;
            }
          }
          if (matchExactAvailable !== -1) return matchExactAvailable;
          if (matchExact !== -1) return matchExact;
          if (matchShadeAvailable !== -1) return matchShadeAvailable;
          if (matchShade !== -1) return matchShade;
          return 0;
        };

        const _normSwapUrl = (u) => {
          if (!u) return '';
          let s = String(u).split('?')[0];
          s = s.replace(/_(pico|icon|thumb|small|compact|medium|large|grande|original|master|\d+x\d*|\d*x\d+)(?=\.[a-z]+$)/i, '');
          return s;
        };

        const swapImage = (src) => {
          if (!imageEl || !src) return;
          const curSrc = imageEl.getAttribute('src') || imageEl.src || '';
          if (curSrc === src || _normSwapUrl(curSrc) === _normSwapUrl(src)) return;
          const gen = (imageEl._swapGen = (imageEl._swapGen || 0) + 1);
          if (!imageEl.src) {
            imageEl.src = src;
            imageEl.removeAttribute('srcset');
            imageEl._initialSwapDone = true;
            return;
          }
          if (!imageEl._initialSwapDone) {
            imageEl.src = src;
            imageEl.removeAttribute('srcset');
            imageEl._initialSwapDone = true;
            return;
          }
          const pre = new Image();
          const commit = () => {
            if (imageEl._swapGen !== gen) return;
            imageEl.classList.add('is-swapping');
            requestAnimationFrame(() => {
              if (imageEl._swapGen !== gen) {
                imageEl.classList.remove('is-swapping');
                return;
              }
              imageEl.src = src;
              imageEl.removeAttribute('srcset');
              const clear = () => {
                if (imageEl._swapGen === gen) imageEl.classList.remove('is-swapping');
              };
              if (imageEl.complete && imageEl.naturalWidth > 0) requestAnimationFrame(clear);
              else {
                imageEl.addEventListener('load', clear, { once: true });
                imageEl.addEventListener('error', clear, { once: true });
              }
            });
          };
          pre.onload = commit;
          pre.onerror = commit;
          pre.src = src;
          if (pre.complete && pre.naturalWidth > 0) commit();
        };

        const renderThumbs = (product, currentVariantSrc, variantCarousel) => {
          if (!thumbsEl) return;
          thumbsEl.innerHTML = '';
          const normUrl = (u) => {
            if (!u) return '';
            let s = String(u).split('?')[0];
            s = s.replace(/_(pico|icon|thumb|small|compact|medium|large|grande|original|master|\d+x\d*|\d*x\d+)(?=\.[a-z]+$)/i, '');
            return s;
          };
          let extras = [];
          if (variantCarousel && variantCarousel.length) {
            extras = variantCarousel.slice();
          } else {
            const variantImageUrls = {};
            (product.variants || []).forEach((v) => {
              const fi = v && v.featured_image;
              let url = null;
              if (typeof fi === 'string') url = fi;
              else if (fi && fi.src) url = fi.src;
              if (url) variantImageUrls[normUrl(url)] = true;
            });
            extras = (product.images || []).filter((src) => !variantImageUrls[normUrl(src)]);
          }
          const currentNorm = normUrl(currentVariantSrc);
          const thumbs = [];
          if (currentVariantSrc) thumbs.push({ type: 'image', src: currentVariantSrc });
          extras.forEach((src) => {
            if (normUrl(src) !== currentNorm) thumbs.push({ type: 'image', src });
          });
          if (!thumbs.length) return;
          thumbs.forEach((t, i) => {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'cclp-plp-quickshop__thumb' + (i === 0 ? ' is-active' : '');
            btn.setAttribute('data-thumb-src', t.src);
            btn.setAttribute('data-thumb-type', 'image');
            btn.setAttribute('aria-label', 'View image ' + (i + 1));
            const img = document.createElement('img');
            img.src = t.src;
            img.alt = '';
            img.loading = 'eager';
            btn.appendChild(img);
            thumbsEl.appendChild(btn);
            if (t.src) {
              const pre = new Image();
              pre.src = t.src;
            }
          });
        };

        const setActiveThumbBySrc = (src) => {
          if (!thumbsEl || !src) return;
          thumbsEl.querySelectorAll('.cclp-plp-quickshop__thumb').forEach((t) => {
            t.classList.toggle('is-active', t.getAttribute('data-thumb-src') === src);
          });
        };

        self._quickshop.helpers = {
          renderStars, fetchReviews, renderReviews, fetchShadeDescriptions,
          optionName, detectShadeOptionIndex, detectSizeOptionIndex, findVariantIndexBy,
          swapImage, renderThumbs, setActiveThumbBySrc,
        };

        self._initQuickShopVariant();
      }

      _initQuickShopVariant() {
        const self = this;
        const section = self._section;
        const qs = self._quickshop;
        if (!qs) return;
        const { quickshopEl, formatPrice, escHtml } = qs;
        const { imageEl, titleEl, priceEl, compareEl, starsEl, descEl, variantEl, variantNameEl,
          swatchesEl, sizesEl, sizeBtnsEl, addBtn, addLabelEl, addCompareEl, addPriceEl, pdpLinks } = qs.els;
        const { swapImage, renderThumbs, setActiveThumbBySrc, detectShadeOptionIndex, detectSizeOptionIndex,
          findVariantIndexBy, fetchShadeDescriptions, renderReviews } = qs.helpers;

        const applyQuickshopGallery = (product, variantIndex) => {
          const variant = product.variants[variantIndex];
          if (!variant) return;
          const cur = qs.getCurrent();
          const vd = cur && cur.variantData ? cur.variantData[String(variant.id)] : null;
          const variantCarousel = vd && vd.carousel ? vd.carousel : null;
          const variantPackshot =
            variantCarousel && variantCarousel.length
              ? variantCarousel[0]
              : (variant.featured_image && variant.featured_image.src) || null;
          if (variantPackshot) swapImage(variantPackshot);
          renderThumbs(product, variantPackshot, variantCarousel);
          if (variantPackshot) setActiveThumbBySrc(variantPackshot);
        };

        const updateQuickshopVariantDescriptor = (product, variantIndex) => {
          const variant = product.variants[variantIndex];
          const cur = qs.getCurrent();
          if (!variant || !cur) return;
          const shadeIdx = cur.shadeIndex;
          const variantName = shadeIdx >= 0 && variant.options ? variant.options[shadeIdx] || variant.title : variant.title;
          if (variantNameEl) variantNameEl.textContent = variantName || '';
          const descSpan = quickshopEl.querySelector('.cclp-plp-quickshop__variant-count');
          if (descSpan) {
            const shadeMap = cur.shadeDescMap || {};
            const lookup = (variantName || '').toLowerCase().trim();
            const desc = shadeMap[lookup] || '';
            descSpan.textContent = desc ? ' ' + desc : '';
          }
        };

        const selectVariant = (product, variantIndex) => {
          const variant = product.variants[variantIndex];
          if (!variant) return;
          const cur = qs.getCurrent();
          if (cur && cur.selectedVariantIndex === variantIndex) return;
          cur.selectedVariantId = variant.id;
          cur.selectedVariantIndex = variantIndex;
          if (cur.shadeIndex >= 0 && variant.options) cur.selectedShade = variant.options[cur.shadeIndex];
          if (cur.sizeIndex >= 0 && variant.options) cur.selectedSize = variant.options[cur.sizeIndex];

          applyQuickshopGallery(product, variantIndex);

          priceEl.textContent = formatPrice(variant.price);
          const hasCompare = variant.compare_at_price && variant.compare_at_price > variant.price;
          compareEl.textContent = hasCompare ? formatPrice(variant.compare_at_price) : '';
          if (addCompareEl) addCompareEl.textContent = hasCompare ? formatPrice(variant.compare_at_price) : '';
          addPriceEl.textContent = formatPrice(variant.price);

          updateQuickshopVariantDescriptor(product, variantIndex);

          const activeShade = cur.selectedShade;
          swatchesEl.querySelectorAll('.cclp-plp-quickshop__swatch').forEach((s) => {
            const byShade = s.getAttribute('data-shade');
            if (byShade !== null) s.classList.toggle('is-active', byShade === activeShade);
            else s.classList.toggle('is-active', parseInt(s.getAttribute('data-variant-index'), 10) === variantIndex);
          });

          if (sizeBtnsEl && cur.sizeIndex >= 0) {
            const activeSize = cur.selectedSize;
            sizeBtnsEl.querySelectorAll('.cclp-plp-quickshop__size-btn').forEach((b) => {
              const sv = b.getAttribute('data-size');
              b.classList.toggle('is-active', sv === activeSize);
              let available = false;
              for (let vi = 0; vi < product.variants.length; vi++) {
                const pv = product.variants[vi];
                if (!pv.available) continue;
                if (cur.shadeIndex >= 0 && pv.options[cur.shadeIndex] !== activeShade) continue;
                if (pv.options[cur.sizeIndex] !== sv) continue;
                available = true;
                break;
              }
              if (available) b.removeAttribute('aria-disabled');
              else b.setAttribute('aria-disabled', 'true');
            });
          }

          if (variant.available) {
            addBtn.removeAttribute('disabled');
            addLabelEl.textContent = 'Add to cart';
          } else {
            addBtn.setAttribute('disabled', 'true');
            addLabelEl.textContent = 'Sold out';
            addPriceEl.textContent = '';
            if (addCompareEl) addCompareEl.textContent = '';
          }
        };

        const renderProduct = (product, triggerBtn) => {
          const shadeIndex = detectShadeOptionIndex(product);
          const sizeIndex = detectSizeOptionIndex(product, shadeIndex);
          qs.setCurrent({
            product,
            selectedVariantId: null,
            selectedVariantIndex: -1,
            shadeIndex,
            sizeIndex,
            selectedShade: null,
            selectedSize: null,
            variantData: {},
          });
          const cur = qs.getCurrent();

          titleEl.textContent = product.title || '';

          const desc = product.description || '';
          const tmp = document.createElement('div');
          tmp.innerHTML = desc;
          let text = (tmp.textContent || '').replace(/\s+/g, ' ').trim();
          if (text.length > 420) text = text.slice(0, 420).replace(/\s+\S*$/, '') + '\u2026';
          descEl.textContent = text;
          descEl.style.display = text ? '' : 'none';

          const pdpUrl = product.url || '/products/' + (product.handle || '');
          pdpLinks.forEach((l) => l.setAttribute('href', pdpUrl));

          renderReviews(product.id);

          fetchShadeDescriptions(product.handle).then((map) => {
            const c = qs.getCurrent();
            if (!c || c.product !== product) return;
            c.shadeDescMap = map;
            const idx = c.selectedVariantIndex;
            if (typeof idx === 'number' && idx >= 0) {
              try { updateQuickshopVariantDescriptor(product, idx); } catch (e) {}
            }
          });

          starsEl.innerHTML = '';
          const cardStars = triggerBtn ? triggerBtn.closest('.cclp-plp-card') && triggerBtn.closest('.cclp-plp-card').querySelector('.product__card-stars') : null;
          if (cardStars && cardStars.innerHTML.trim()) {
            starsEl.innerHTML = cardStars.innerHTML;
            starsEl.querySelectorAll('.junip-product-summary-review-count').forEach((el) => {
              const txt = (el.textContent || '').trim();
              const m = txt.match(/[0-9]+/);
              if (m) el.textContent = m[0] + ' Reviews';
            });
          } else {
            starsEl.innerHTML = '<span class="junip-product-summary" data-product-id="' + product.id + '"></span>';
            if (window.junip && typeof window.junip.storefrontStarsLoader === 'function') {
              try { window.junip.storefrontStarsLoader(); } catch (e) {}
            }
          }

          swatchesEl.innerHTML = '';
          swatchesEl._qsHoverSwatch = null;
          const variantSwatchData = {};
          const fallbackStyleById = {};
          if (triggerBtn) {
            const card = triggerBtn.closest('.cclp-plp-card');
            if (card) {
              const dataScript = card.querySelector('.cclp-plp-card__quickshop-variants');
              if (dataScript) {
                try {
                  const parsed = JSON.parse(dataScript.textContent);
                  if (parsed && parsed.forEach) {
                    parsed.forEach((vd) => {
                      if (vd && vd.id !== undefined) variantSwatchData[String(vd.id)] = vd;
                    });
                  }
                } catch (e) {}
              }
              cur.variantData = variantSwatchData;
              card.querySelectorAll('.cclp-plp-card__swatch').forEach((cs) => {
                const vid = cs.getAttribute('data-variant-id');
                if (!vid) return;
                const inner = cs.querySelector('i');
                if (inner) fallbackStyleById[vid] = inner.getAttribute('style') || '';
              });
            }
          }

          const hasShades = shadeIndex >= 0 && product.variants && product.variants.length > 1;
          const hasSizes = sizeIndex >= 0 && product.variants && product.variants.length > 1;

          if (hasShades) {
            const seenShades = {};
            product.variants.forEach((v, j) => {
              const shadeName = v.options && v.options[shadeIndex] ? v.options[shadeIndex] : v.title;
              if (Object.prototype.hasOwnProperty.call(seenShades, shadeName)) return;
              seenShades[shadeName] = j;
              const btn = document.createElement('button');
              btn.type = 'button';
              btn.className = 'cclp-plp-quickshop__swatch';
              btn.setAttribute('data-variant-index', j);
              btn.setAttribute('data-variant-id', v.id);
              btn.setAttribute('data-shade', shadeName);
              btn.title = shadeName;
              btn.setAttribute('aria-label', 'Select ' + shadeName);

              let anyAvailable = false;
              for (let vi = 0; vi < product.variants.length; vi++) {
                const pv = product.variants[vi];
                if (pv.options[shadeIndex] === shadeName && pv.available) { anyAvailable = true; break; }
              }
              if (!anyAvailable) btn.setAttribute('aria-disabled', 'true');

              const i = document.createElement('i');
              const sd = variantSwatchData[String(v.id)];
              const fi = v && v.featured_image;
              let variantImg = null;
              if (typeof fi === 'string') variantImg = fi;
              else if (fi && fi.src) variantImg = fi.src;
              let bgStyle;
              if (sd && sd.image) bgStyle = "background: #eee url('" + sd.image + "') center/cover no-repeat;";
              else if (sd && sd.color) bgStyle = 'background: ' + sd.color + ';';
              else if (fallbackStyleById[String(v.id)]) bgStyle = fallbackStyleById[String(v.id)];
              else if (variantImg) bgStyle = "background: #eee url('" + variantImg + "') center/cover no-repeat;";
              else bgStyle = 'background: #e8e4dc;';
              i.setAttribute('style', bgStyle);
              btn.appendChild(i);
              swatchesEl.appendChild(btn);
            });
            variantEl.hidden = false;
          } else {
            variantEl.hidden = true;
          }

          if (sizeBtnsEl) sizeBtnsEl.innerHTML = '';
          if (hasSizes && sizesEl && sizeBtnsEl) {
            const sizeValues = [];
            const seenSizes = {};
            product.variants.forEach((v) => {
              const sv = v.options && v.options[sizeIndex];
              if (sv && !seenSizes[sv]) { seenSizes[sv] = true; sizeValues.push(sv); }
            });
            sizeValues.forEach((sv) => {
              const sb = document.createElement('button');
              sb.type = 'button';
              sb.className = 'cclp-plp-quickshop__size-btn';
              sb.setAttribute('data-size', sv);
              sb.setAttribute('aria-label', 'Select size ' + sv);
              sb.textContent = sv;
              sizeBtnsEl.appendChild(sb);
            });
            sizesEl.hidden = false;
          } else if (sizesEl) {
            sizesEl.hidden = true;
          }

          if (triggerBtn) {
            const cardImg =
              triggerBtn.closest('.cclp-plp-card').querySelector('.product__card-image.has-hover') ||
              triggerBtn.closest('.cclp-plp-card').querySelector('.product__card-image');
            if (cardImg && cardImg.currentSrc) imageEl.src = cardImg.currentSrc;
            else if (cardImg && cardImg.src) imageEl.src = cardImg.src;
          }
          if (!imageEl.src && product.featured_image) imageEl.src = product.featured_image;
          imageEl.alt = product.title || '';

          let initialIdx = 0;
          for (let k = 0; k < product.variants.length; k++) {
            if (product.variants[k].available) { initialIdx = k; break; }
          }
          selectVariant(product, initialIdx);
          qs.showContent();
        };

        self._quickshop.api = { selectVariant, renderProduct, applyQuickshopGallery, updateQuickshopVariantDescriptor };

        self._initQuickShopEvents();
      }

      _initQuickShopEvents() {
        const self = this;
        const section = self._section;
        const qs = self._quickshop;
        if (!qs) return;
        const { quickshopEl, quickshopCache, quickshopInflight } = qs;
        const { swatchesEl, sizesEl, sizeBtnsEl, thumbsEl, addBtn, addLabelEl, addPriceEl } = qs.els;
        const { swapImage, setActiveThumbBySrc, findVariantIndexBy } = qs.helpers;
        const { selectVariant, renderProduct, applyQuickshopGallery, updateQuickshopVariantDescriptor } = qs.api;

        // Variants hidden via the custom.hide_from_online_store metafield are absent
        // from the ?view=json-data payload; /products/{handle}.js cannot expose
        // metafields, so intersect the two. Fails open (unfiltered) on any error.
        const filterHiddenVariants = (product, handle) =>
          fetch('/products/' + handle + '?view=json-data', { headers: { Accept: 'application/json' } })
            .then((r) => (r.ok ? r.json() : null))
            .then((data) => {
              if (data && Array.isArray(data.variants) && product && Array.isArray(product.variants)) {
                const hidden = {};
                data.variants.forEach((v) => { if (v && v.id != null && v.hidden === true) hidden[String(v.id)] = true; });
                const filtered = product.variants.filter((v) => !hidden[String(v.id)]);
                if (filtered.length) product.variants = filtered;
              }
              return product;
            })
            .catch(() => product);

        const fetchProduct = (handle) => {
          if (quickshopCache[handle]) return Promise.resolve(quickshopCache[handle]);
          if (quickshopInflight[handle]) return quickshopInflight[handle];
          const p = fetch('/products/' + handle + '.js', { headers: { Accept: 'application/json' } })
            .then((r) => {
              if (!r.ok) throw new Error('fetch failed');
              return r.json();
            })
            .then((product) => filterHiddenVariants(product, handle))
            .then((product) => {
              quickshopCache[handle] = product;
              return product;
            })
            .finally(() => {
              delete quickshopInflight[handle];
            });
          quickshopInflight[handle] = p;
          return p;
        };

        const prePaintCardImage = (triggerBtn) => {
          if (!triggerBtn || !qs.els.imageEl) return;
          const card = triggerBtn.closest('.cclp-plp-card');
          if (!card) return;
          const cardImg =
            card.querySelector('.product__card-image.has-hover') || card.querySelector('.product__card-image');
          if (!cardImg) return;
          const src = cardImg.currentSrc || cardImg.src;
          if (src && qs.els.imageEl.getAttribute('src') !== src) {
            qs.els.imageEl.src = src;
            qs.els.imageEl.removeAttribute('srcset');
          }
        };

        const openForHandle = (handle, triggerBtn) => {
          qs.setLastTrigger(triggerBtn || null);
          qs.openModal();
          if (quickshopCache[handle]) {
            renderProduct(quickshopCache[handle], triggerBtn);
            return;
          }
          prePaintCardImage(triggerBtn);
          qs.showLoading();
          fetchProduct(handle)
            .then((product) => renderProduct(product, triggerBtn))
            .catch(() => {
              qs.els.pdpLinks.forEach((l) => l.setAttribute('href', '/products/' + handle));
              qs.showError();
            });
        };

        const refreshCartAndOpenDrawer = () => {
          if (typeof window.JR?.refreshCartAndOpen === 'function') {
            window.JR.refreshCartAndOpen();
            return;
          }
          if (window.liquidAjaxCart && typeof window.liquidAjaxCart.update === 'function') {
            window.liquidAjaxCart.update({}, {});
          }
          if (typeof window.JR?.refreshAjaxCartSections === 'function') {
            window.JR.refreshAjaxCartSections();
          }
          window.dispatchEvent(new CustomEvent('cart-open'));
        };

        const addToCart = () => {
          const cur = qs.getCurrent();
          if (!cur || !cur.selectedVariantId) return;
          const variantId = parseInt(cur.selectedVariantId, 10);
          if (!variantId) return;

          addBtn.setAttribute('disabled', 'true');
          const savedLabel = addLabelEl.textContent;
          const savedPrice = addPriceEl.textContent;
          addLabelEl.textContent = 'Adding\u2026';
          addPriceEl.textContent = '';

          const onSuccess = () => {
            addBtn.removeAttribute('disabled');
            addLabelEl.textContent = savedLabel;
            addPriceEl.textContent = savedPrice;
            refreshCartAndOpenDrawer();
            qs.closeModal();
          };
          const onFail = () => {
            addBtn.removeAttribute('disabled');
            addLabelEl.textContent = 'Try again';
            addPriceEl.textContent = savedPrice;
          };

          const payload = { id: variantId, quantity: 1 };

          try {
            if (typeof window.addToCart === 'function') {
              const result = window.addToCart(payload, onSuccess);
              if (result && typeof result.catch === 'function') result.catch(onFail);
              return;
            }
          } catch (e) {}

          const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

          fetch(root + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
          })
            .then((r) => {
              if (!r.ok) throw new Error('cart add failed');
              return r.json();
            })
            .then(onSuccess)
            .catch(onFail);
        };

        const handleQuickshopTrigger = (e) => {
          const btn = e.target && e.target.closest ? e.target.closest('.cclp-plp-card__quickshop-btn') : null;
          if (!btn || !section.contains(btn)) return;
          e.stopPropagation();
          if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
          if (e.type === 'click') {
            e.preventDefault();
            const handle = btn.getAttribute('data-quickshop-handle');
            if (handle) openForHandle(handle, btn);
          }
        };
        ['pointerdown', 'mousedown', 'click'].forEach((type) => {
          section.addEventListener(type, handleQuickshopTrigger, { capture: true, passive: false });
        });

        const bindQuickshopClickHandlers = () => {
          section.querySelectorAll('.cclp-plp-card__quickshop-btn').forEach((btn) => {
            if (btn.__cclpQuickshopBound) return;
            btn.__cclpQuickshopBound = true;
            btn.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();
              if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
              const handle = btn.getAttribute('data-quickshop-handle');
              if (handle) openForHandle(handle, btn);
            });
          });
        };
        bindQuickshopClickHandlers();
        self._bindQuickshopClickHandlers = bindQuickshopClickHandlers;

        const obs = new MutationObserver(() => {
          bindQuickshopClickHandlers();
          if (typeof self._bindSwatchClickHandlers === 'function') self._bindSwatchClickHandlers();
        });
        obs.observe(section, { childList: true, subtree: true });
        self._quickshopBindObs = obs;

        quickshopEl.querySelectorAll('[data-quickshop-close]').forEach((el) => {
          el.addEventListener('click', (e) => {
            e.preventDefault();
            qs.closeModal();
          });
        });

        const selectByShadeSize = (product, shadeValue, sizeValue) => {
          const cur = qs.getCurrent();
          if (!cur || cur.product !== product) return;
          const idx = findVariantIndexBy(product, cur.shadeIndex, shadeValue, cur.sizeIndex, sizeValue);
          selectVariant(product, idx);
        };

        swatchesEl.addEventListener('click', (e) => {
          const sw = e.target.closest('.cclp-plp-quickshop__swatch');
          if (!sw || sw.getAttribute('aria-disabled') === 'true') return;
          const cur = qs.getCurrent();
          if (!cur || !cur.product) return;
          const shadeVal = sw.getAttribute('data-shade');
          if (shadeVal !== null) {
            selectByShadeSize(cur.product, shadeVal, cur.selectedSize);
            return;
          }
          const idx = parseInt(sw.getAttribute('data-variant-index'), 10);
          if (!isNaN(idx)) selectVariant(cur.product, idx);
        });

        swatchesEl.addEventListener('mouseover', (e) => {
          if (!qs.canHover) return;
          if (!document.documentElement.classList.contains('has-mouse')) return;
          const sw = e.target.closest('.cclp-plp-quickshop__swatch');
          if (!sw || sw.getAttribute('aria-disabled') === 'true') return;
          const cur = qs.getCurrent();
          if (!cur || !cur.product) return;
          if (sw === swatchesEl._qsHoverSwatch) return;
          swatchesEl._qsHoverSwatch = sw;
          const product = cur.product;
          let previewIdx = -1;
          const shadeVal = sw.getAttribute('data-shade');
          if (shadeVal !== null) {
            previewIdx = findVariantIndexBy(product, cur.shadeIndex, shadeVal, cur.sizeIndex, cur.selectedSize);
          } else {
            const idx = parseInt(sw.getAttribute('data-variant-index'), 10);
            if (!isNaN(idx)) previewIdx = idx;
          }
          if (previewIdx < 0) return;
          applyQuickshopGallery(product, previewIdx);
          updateQuickshopVariantDescriptor(product, previewIdx);
        });

        swatchesEl.addEventListener(
          'mouseout',
          (e) => {
            if (!qs.canHover) return;
            if (!document.documentElement.classList.contains('has-mouse')) return;
            if (e.relatedTarget && swatchesEl.contains(e.relatedTarget)) return;
            swatchesEl._qsHoverSwatch = null;
            const cur = qs.getCurrent();
            if (!cur || !cur.product) return;
            const idx = cur.selectedVariantIndex;
            if (typeof idx !== 'number' || idx < 0) return;
            applyQuickshopGallery(cur.product, idx);
            updateQuickshopVariantDescriptor(cur.product, idx);
          },
          { passive: true }
        );

        if (sizeBtnsEl) {
          sizeBtnsEl.addEventListener('click', (e) => {
            const b = e.target.closest('.cclp-plp-quickshop__size-btn');
            if (!b || b.getAttribute('aria-disabled') === 'true') return;
            const cur = qs.getCurrent();
            if (!cur || !cur.product) return;
            const sv = b.getAttribute('data-size');
            if (sv === null) return;
            selectByShadeSize(cur.product, cur.selectedShade, sv);
          });
        }

        const prefetchHandle = (card) => {
          if (!card || !section.contains(card)) return;
          const btn = card.querySelector('.cclp-plp-card__quickshop-btn');
          if (!btn) return;
          const handle = btn.getAttribute('data-quickshop-handle');
          if (!handle || quickshopCache[handle]) return;
          fetch('/products/' + handle + '.js', { headers: { Accept: 'application/json' } })
            .then((r) => (r.ok ? r.json() : null))
            .then((product) => {
              if (product) quickshopCache[handle] = product;
            })
            .catch(() => {});
        };
        section.addEventListener(
          'mouseenter',
          (e) => {
            const card = e.target && e.target.closest ? e.target.closest('.cclp-plp-card[data-variant="v3"]') : null;
            if (card) prefetchHandle(card);
          },
          true
        );
        section.addEventListener(
          'touchstart',
          (e) => {
            const card = e.target && e.target.closest ? e.target.closest('.cclp-plp-card[data-variant="v3"]') : null;
            if (card) prefetchHandle(card);
          },
          { capture: true, passive: true }
        );

        if (thumbsEl) {
          thumbsEl.addEventListener('click', (e) => {
            const thumb = e.target.closest('.cclp-plp-quickshop__thumb');
            if (!thumb) return;
            const src = thumb.getAttribute('data-thumb-src');
            if (!src) return;
            swapImage(src);
            setActiveThumbBySrc(src);
          });
        }

        addBtn.addEventListener('click', (e) => {
          e.preventDefault();
          addToCart();
        });

        self._keydownHandler = (e) => {
          if (e.key === 'Escape' && quickshopEl.getAttribute('data-open') === 'true') qs.closeModal();
        };
        document.addEventListener('keydown', self._keydownHandler);
      }

      disconnectedCallback() {
        if (this._keydownHandler) {
          document.removeEventListener('keydown', this._keydownHandler);
          this._keydownHandler = null;
        }
        if (this._observer) {
          try {
            this._observer.disconnect();
          } catch (e) {}
          this._observer = null;
        }
        if (this._quickshopBindObs) {
          try {
            this._quickshopBindObs.disconnect();
          } catch (e) {}
          this._quickshopBindObs = null;
        }
        this._initialized = false;
      }
    }
  );
})();

